// Shared plumbing for the job-based Compartment Report backend (see
// backend/api/compartment_report.js): every computation (Validate, DB fetch,
// Calculate, Report, exports, manual-exclusion preview/save, reproduce,
// duplicate) is an on-demand job - POST .../reportings/:id/jobs starts it,
// GET /jobs/:jobId is polled every 2 s until it succeeds or fails. Large
// files move browser <-> S3 directly via presigned URLs (uploadFile /
// downloadUrl below), never through exaport's backend.
//
// WizardJobContext lets PreflightWizardPage own the polling, so a job keeps
// being watched (and its progress banner stays up) when the tab that started
// it is unmounted by a TabView switch.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axiosWithAuth from '../../helpers/AxiosHelper';
import errorMessage from '../../helpers/errorMessage';

export const API = '/compartment-report';
export const POLL_INTERVAL_MS = 2000;
export const NOT_VALIDATED_DETAIL = 'Not computed yet - run "Validate Data" first.';

// Human-readable name per job action, for progress banners.
export const ACTION_LABELS = {
  validate: 'Validating data',
  db_fetch: 'Fetching from database',
  exclusions_preview: 'Previewing exclusion rule',
  exclusions_save: 'Saving manual exclusions',
  calculate: 'Calculating',
  report: 'Generating reporting',
  export: 'Preparing export',
  reproduce: 'Reproducing reporting',
  duplicate: 'Duplicating reporting',
};

// Error carrying the backend's {detail} and HTTP-ish status (a failed job's
// error / error_status, or an axios error's response).
export class JobError extends Error {
  constructor(detail, status = null, extra = {}) {
    super(detail || 'Request failed');
    this.detail = detail || 'Request failed';
    this.status = status;
    Object.assign(this, extra);
  }
}

const cancelledError = () => new JobError('Cancelled', null, { cancelled: true });

// Best-effort user-facing message for any error thrown by axios/fetch/jobs.
export function apiErrorDetail(err, fallback = 'Request failed.') {
  if (!err) return fallback;
  if (err instanceof JobError) return err.detail;
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (detail) return fallback;
  return errorMessage(err, fallback);
}

// For read endpoints that 409 until "Validate Data" has run: the backend's
// own message for a 409, otherwise the component's generic fallback.
export function readErrorDetail(err, fallback) {
  if (err?.response?.status === 409) return err.response.data?.detail || NOT_VALIDATED_DETAIL;
  return errorMessage(err, fallback);
}

const toJobError = (err, fallback) => {
  if (err instanceof JobError) return err;
  return new JobError(apiErrorDetail(err, fallback), err?.response?.status ?? null, {
    jobId: err?.response?.data?.job_id || null,
  });
};

export async function startJob(reportingId, action, params = {}) {
  try {
    const res = await axiosWithAuth.post(`${API}/reportings/${reportingId}/jobs`, { action, params });
    return res.data;
  } catch (err) {
    console.error(`[jobs] start ${action} error:`, errorMessage(err));
    throw toJobError(err, `Could not start ${action}.`);
  }
}

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(cancelledError());
    const handle = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(handle);
      reject(cancelledError());
    });
  });

// Polls GET /jobs/:jobId until succeeded/failed. Resolves with the finished
// job doc on success, rejects with a JobError(error, error_status) on
// failure, or with JobError{cancelled: true} once `signal` aborts.
export async function pollJob(jobId, onUpdate, signal) {
  let transientFailures = 0;
  for (;;) {
    if (signal?.aborted) throw cancelledError();
    let job;
    try {
      job = (await axiosWithAuth.get(`${API}/jobs/${jobId}`)).data;
      transientFailures = 0;
    } catch (err) {
      if (signal?.aborted) throw cancelledError();
      transientFailures += 1;
      // A couple of network blips shouldn't abandon a multi-minute job.
      if (transientFailures > 3 || err?.response?.status === 404) {
        console.error('[jobs] poll error:', errorMessage(err));
        throw toJobError(err, 'Lost track of the job.');
      }
      await sleep(POLL_INTERVAL_MS, signal);
      continue;
    }
    if (signal?.aborted) throw cancelledError();
    if (onUpdate) onUpdate(job);
    if (job.status === 'succeeded') return job;
    if (job.status === 'failed') {
      throw new JobError(job.error || 'Job failed', job.error_status ?? null, { job });
    }
    await sleep(POLL_INTERVAL_MS, signal);
  }
}

export async function runJob(reportingId, action, params = {}, { onUpdate, signal } = {}) {
  const job = await startJob(reportingId, action, params);
  if (onUpdate) onUpdate(job);
  return pollJob(job.job_id, onUpdate, signal);
}

// Hook: runs/attaches to one job at a time, exposes its latest doc for a
// progress indicator, and stops polling on unmount.
export function useJobRunner() {
  const [job, setJob] = useState(null);
  const controllerRef = useRef(null);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, []);

  const track = useCallback(async (promiseFactory) => {
    const controller = new AbortController();
    controllerRef.current = controller;
    busyRef.current = true;
    const onUpdate = (doc) => {
      if (mountedRef.current && !controller.signal.aborted) setJob(doc);
    };
    try {
      return await promiseFactory(onUpdate, controller.signal);
    } finally {
      busyRef.current = false;
      if (controllerRef.current === controller) controllerRef.current = null;
      if (mountedRef.current) setJob(null);
    }
  }, []);

  const run = useCallback(
    (reportingId, action, params = {}) => {
      if (busyRef.current) {
        return Promise.reject(new JobError('Another job is already running - wait for it to finish.', 409));
      }
      return track((onUpdate, signal) => runJob(reportingId, action, params, { onUpdate, signal }));
    },
    [track]
  );

  const attach = useCallback(
    (jobDoc) => {
      if (busyRef.current) return Promise.reject(new JobError('Already tracking a job.', 409));
      return track((onUpdate, signal) => {
        onUpdate(jobDoc);
        return pollJob(jobDoc.job_id, onUpdate, signal);
      });
    },
    [track]
  );

  const isBusy = useCallback(() => busyRef.current, []);

  return { job, running: !!job, run, attach, isBusy };
}

// ---------------------------------------------------------------------------
// Wizard-level job context
// ---------------------------------------------------------------------------

export const WizardJobContext = createContext(null);

// Inside the wizard: the wizard's shared runner (survives tab switches). The
// returned runJob rejects with {cancelled: true} if the CALLING component
// has unmounted by the time the job finishes, so callers can skip setState.
// Outside the wizard: a component-local runner.
export function useWizardJob() {
  const ctx = useContext(WizardJobContext);
  const local = useJobRunner();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const source = ctx || { runJob: local.run, activeJob: local.job };
  const baseRun = source.runJob;
  const runJobGuarded = useCallback(
    (reportingId, action, params) =>
      baseRun(reportingId, action, params).then(
        (job) => {
          if (!mountedRef.current) throw cancelledError();
          return job;
        },
        (err) => {
          if (!mountedRef.current) throw cancelledError();
          throw err;
        }
      ),
    [baseRun]
  );

  return { runJob: runJobGuarded, activeJob: source.activeJob, busy: !!source.activeJob };
}

export const WizardJobProvider = ({ value, children }) => (
  <WizardJobContext.Provider value={value}>{children}</WizardJobContext.Provider>
);

// ---------------------------------------------------------------------------
// Uploads / downloads
// ---------------------------------------------------------------------------

// presign -> PUT straight to S3 (plain fetch, no auth header) -> complete.
// Resolves with the /complete response; rejects with a JobError{detail}.
export async function uploadFile(reportingId, field, file, { autoDetect = false } = {}) {
  let presigned;
  try {
    presigned = (await axiosWithAuth.post(`${API}/reportings/${reportingId}/uploads/presign`, { field, filename: file.name })).data;
  } catch (err) {
    console.error(`[uploadFile] presign error (${field}):`, errorMessage(err));
    throw toJobError(err, 'Upload failed.');
  }

  let putRes;
  try {
    putRes = await fetch(presigned.url, { method: 'PUT', body: file });
  } catch (err) {
    console.error(`[uploadFile] storage PUT error (${field}):`, errorMessage(err));
    throw new JobError('Upload to storage failed.');
  }
  if (!putRes.ok) {
    console.error(`[uploadFile] storage PUT HTTP ${putRes.status} (${field})`);
    throw new JobError(`Upload to storage failed (HTTP ${putRes.status})`, putRes.status);
  }

  try {
    const body = { field, filename: file.name };
    if (autoDetect) body.auto_detect = true;
    return (await axiosWithAuth.post(`${API}/reportings/${reportingId}/uploads/complete`, body)).data;
  } catch (err) {
    console.error(`[uploadFile] complete error (${field}):`, errorMessage(err));
    throw toJobError(err, 'Upload failed.');
  }
}

// Presigned URLs already carry Content-Disposition: attachment, so a plain
// anchor navigation downloads without leaving the page.
export function downloadUrl(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// Runs an `export` job and downloads its file.
export async function runExport(runJobFn, reportingId, params) {
  const job = await runJobFn(reportingId, 'export', params);
  if (!job.download_url) throw new JobError('Export finished but no download link was returned.');
  downloadUrl(job.download_url, job.result?.filename);
  return job;
}
